// Cola de transcripción — procesa un archivo a la vez (la inferencia WASM ya
// es pesada por sí sola; no tiene sentido correr varias en paralelo en el
// mismo tab) y sobrevive a la navegación entre páginas de la SPA porque vive
// a nivel de módulo, no atado al ciclo de vida de ningún componente — ver
// openspec/changes/video-multi-audio-tracks design.md §5 (Riesgos: "un tab
// cerrado simplemente significa que no hay captions todavía").
import { useSyncExternalStore } from "react"
import api from "./api"
import { transcribeAudioFile } from "./transcription"

export type TranscriptionState = "queued" | "transcribing" | "done" | "error"

type Job = { videoId: string; trackId: string; file: File }

const state = new Map<string, TranscriptionState>()
const listeners = new Set<() => void>()
const queue: Job[] = []
let processing = false

function notify() {
  for (const listener of listeners) listener()
}

function setState(trackId: string, next: TranscriptionState) {
  state.set(trackId, next)
  notify()
}

async function processQueue() {
  if (processing) return
  processing = true
  try {
    while (queue.length > 0) {
      const job = queue.shift()
      if (!job) continue
      setState(job.trackId, "transcribing")
      try {
        const vtt = await transcribeAudioFile(job.file)
        await api.put(`/academy/admin/videos/${job.videoId}/audio-tracks/${job.trackId}/captions`, { vtt })
        setState(job.trackId, "done")
      } catch (err) {
        console.error(`[transcription] Track ${job.trackId} failed:`, err)
        setState(job.trackId, "error")
      }
    }
  } finally {
    processing = false
  }
}

export function enqueueTranscription(job: Job): void {
  setState(job.trackId, "queued")
  queue.push(job)
  void processQueue()
}

export function getTranscriptionState(trackId: string): TranscriptionState | undefined {
  return state.get(trackId)
}

export function subscribeTranscription(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Hook reactivo para mostrar el estado de transcripción de un track en la UI. */
export function useTranscriptionState(trackId: string): TranscriptionState | undefined {
  return useSyncExternalStore(subscribeTranscription, () => getTranscriptionState(trackId))
}
